import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';
import ArticleIcon from '@mui/icons-material/Article';
import CommentIcon from '@mui/icons-material/Comment';
import { Link as RouterLink } from 'react-router-dom';
import {
  collection, collectionGroup, getDocs, doc, updateDoc, deleteDoc, setDoc,
  getCountFromServer, Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth, UserRole } from '../context/AuthContext';
import { projects } from '../data/projects';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserRow {
  uid: string;
  name: string;
  email: string;
  role: UserRole;
  createdAt: Timestamp | null;
}

interface PostRow {
  id: string;
  title: string;
  authorName: string;
  createdAt: Timestamp | null;
  published: boolean;
}

interface Stats {
  users: number;
  posts: number;
  comments: number;
}

interface DeleteDialog {
  open: boolean;
  type: 'user' | 'post';
  id: string;
  label: string;
}

type SnackState = { open: boolean; message: string; severity: 'success' | 'error' };

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLE_COLORS: Record<UserRole, 'error' | 'warning' | 'default'> = {
  admin: 'error',
  author: 'warning',
  member: 'default',
};

const ROLE_ORDER: UserRole[] = ['admin', 'author', 'member'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(ts: Timestamp | null): string {
  if (!ts) return '—';
  return ts.toDate().toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ icon, label, value, loading }: {
  icon: React.ReactNode;
  label: string;
  value: number;
  loading: boolean;
}) {
  return (
    <Paper variant="outlined" sx={{ flex: 1, p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
          {loading ? <Skeleton width={32} /> : value}
        </Typography>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
      </Box>
    </Paper>
  );
}

function TableSkeleton({ cols }: { cols: number }) {
  return (
    <>
      {Array.from({ length: 4 }).map((_, i) => (
        <TableRow key={i}>
          {Array.from({ length: cols }).map((__, j) => (
            <TableCell key={j}><Skeleton variant="text" width={j === 0 ? 160 : 100} /></TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AdminDashboardPage() {
  const { authUser, currentUser } = useAuth();

  const [tab, setTab] = React.useState<'users' | 'posts' | 'projects'>('users');
  const [users, setUsers] = React.useState<UserRow[]>([]);
  const [posts, setPosts] = React.useState<PostRow[]>([]);
  const [stats, setStats] = React.useState<Stats>({ users: 0, posts: 0, comments: 0 });
  const [loading, setLoading] = React.useState(true);
  const [fetchError, setFetchError] = React.useState('');

  // slug → postId of what's saved in Firestore
  const [savedLinks, setSavedLinks] = React.useState<Record<string, string>>({});
  // slug → postId of what's currently selected in the UI
  const [linkSelections, setLinkSelections] = React.useState<Record<string, string>>({});
  const [savingSlug, setSavingSlug] = React.useState<string | null>(null);

  const [updatingUid, setUpdatingUid] = React.useState<string | null>(null);
  const [togglingPostId, setTogglingPostId] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  const [deleteDialog, setDeleteDialog] = React.useState<DeleteDialog>({
    open: false, type: 'user', id: '', label: '',
  });
  const [snack, setSnack] = React.useState<SnackState>({ open: false, message: '', severity: 'success' });

  // ── Data fetching ────────────────────────────────────────────────────────────

  React.useEffect(() => {
    let cancelled = false;

    async function fetchAll() {
      setLoading(true);
      setFetchError('');
      try {
        const [userSnap, postSnap, commentCount] = await Promise.all([
          getDocs(collection(db, 'users')),
          getDocs(collection(db, 'posts')),
          getCountFromServer(collectionGroup(db, 'comments')),
        ]);

        if (cancelled) return;

        // Fetch project links separately so a missing/undeployed rule doesn't
        // break the whole dashboard.
        let linkSnap = { docs: [] as { id: string; data: () => Record<string, string> }[] };
        try {
          linkSnap = await getDocs(collection(db, 'projectLinks'));
        } catch {
          // rules not yet deployed or collection doesn't exist — safe to ignore
        }

        const userRows: UserRow[] = userSnap.docs.map(d => {
          const data = d.data();
          return {
            uid: d.id,
            name: data.name ?? '(no name)',
            email: data.email ?? '—',
            role: (data.role as UserRole) ?? 'member',
            createdAt: data.createdAt ?? null,
          };
        });
        userRows.sort((a, b) => {
          const diff = ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role);
          return diff !== 0 ? diff : a.name.localeCompare(b.name);
        });

        const postRows: PostRow[] = postSnap.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            title: data.title ?? '(untitled)',
            authorName: data.authorName ?? 'Unknown',
            createdAt: data.createdAt ?? null,
            published: data.published ?? false,
          };
        });
        postRows.sort((a, b) => {
          if (a.createdAt && b.createdAt) return b.createdAt.seconds - a.createdAt.seconds;
          return 0;
        });

        const links: Record<string, string> = {};
        linkSnap.docs.forEach(d => { links[d.id] = d.data().postId ?? ''; });
        setSavedLinks(links);
        setLinkSelections(links);

        setUsers(userRows);
        setPosts(postRows);
        setStats({
          users: userRows.length,
          posts: postRows.length,
          comments: commentCount.data().count,
        });
      } catch (err) {
        if (!cancelled) setFetchError('Failed to load data. Refresh to try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchAll();
    return () => { cancelled = true; };
  }, []);

  // ── User actions ─────────────────────────────────────────────────────────────

  const handleRoleChange = async (uid: string, newRole: UserRole) => {
    setUpdatingUid(uid);
    try {
      await updateDoc(doc(db, 'users', uid), { role: newRole });
      setUsers(prev => prev.map(u => u.uid === uid ? { ...u, role: newRole } : u));
      setSnack({ open: true, message: 'Role updated.', severity: 'success' });
    } catch {
      setSnack({ open: true, message: 'Failed to update role.', severity: 'error' });
    } finally {
      setUpdatingUid(null);
    }
  };

  // ── Post actions ─────────────────────────────────────────────────────────────

  const handleTogglePublished = async (postId: string, current: boolean) => {
    setTogglingPostId(postId);
    try {
      await updateDoc(doc(db, 'posts', postId), { published: !current });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, published: !current } : p));
      setSnack({ open: true, message: `Post ${!current ? 'published' : 'unpublished'}.`, severity: 'success' });
    } catch {
      setSnack({ open: true, message: 'Failed to update post status.', severity: 'error' });
    } finally {
      setTogglingPostId(null);
    }
  };

  // ── Project link actions ─────────────────────────────────────────────────────

  const handleSaveProjectLink = async (slug: string) => {
    setSavingSlug(slug);
    const postId = linkSelections[slug] ?? '';
    try {
      if (postId) {
        const post = posts.find(p => p.id === postId);
        await setDoc(doc(db, 'projectLinks', slug), { postId, postTitle: post?.title ?? '' });
      } else {
        await deleteDoc(doc(db, 'projectLinks', slug));
      }
      setSavedLinks(prev => ({ ...prev, [slug]: postId }));
      setSnack({ open: true, message: 'Project link saved.', severity: 'success' });
    } catch {
      setSnack({ open: true, message: 'Failed to save link.', severity: 'error' });
    } finally {
      setSavingSlug(null);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────────

  const openDeleteDialog = (type: 'user' | 'post', id: string, label: string) => {
    setDeleteDialog({ open: true, type, id, label });
  };

  const handleDeleteConfirm = async () => {
    const { type, id, label } = deleteDialog;
    setDeleting(true);
    try {
      if (type === 'user') {
        await deleteDoc(doc(db, 'users', id));
        setUsers(prev => prev.filter(u => u.uid !== id));
        setStats(s => ({ ...s, users: s.users - 1 }));
        setSnack({ open: true, message: `User "${label}" removed from database.`, severity: 'success' });
      } else {
        await deleteDoc(doc(db, 'posts', id));
        setPosts(prev => prev.filter(p => p.id !== id));
        setStats(s => ({ ...s, posts: s.posts - 1 }));
        setSnack({ open: true, message: `Post "${label}" deleted.`, severity: 'success' });
      }
      setDeleteDialog(d => ({ ...d, open: false }));
    } catch {
      setSnack({ open: true, message: 'Delete failed. Please try again.', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1024, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700 }} gutterBottom>
        Admin Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {currentUser?.email}
      </Typography>

      {fetchError ? (
        <Alert severity="error">{fetchError}</Alert>
      ) : (
        <>
          {/* Stats cards */}
          <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
            <StatCard icon={<PeopleIcon />} label="Total users" value={stats.users} loading={loading} />
            <StatCard icon={<ArticleIcon />} label="Total posts" value={stats.posts} loading={loading} />
            <StatCard icon={<CommentIcon />} label="Total comments" value={stats.comments} loading={loading} />
          </Box>

          {/* Tabs */}
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={`Users (${stats.users})`} value="users" />
            <Tab label={`Posts (${stats.posts})`} value="posts" />
            <Tab label={`Projects (${projects.length})`} value="projects" />
          </Tabs>

          {/* ── Users tab ── */}
          {tab === 'users' && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" aria-label="User management table">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Joined</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableSkeleton cols={5} />
                  ) : (
                    users.map(user => {
                      const isSelf = user.uid === authUser?.uid;
                      const isUpdating = updatingUid === user.uid;
                      return (
                        <TableRow key={user.uid} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                                {user.name[0]?.toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, lineHeight: 1.2 }}
                                  component={RouterLink}
                                  to={`/profile/${user.uid}`}
                                  style={{ color: 'inherit' }}
                                >
                                  {user.name}
                                </Typography>
                                {isSelf && (
                                  <Typography variant="caption" color="text.secondary">(you)</Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" color="text.secondary">{formatDate(user.createdAt)}</Typography>
                          </TableCell>

                          <TableCell>
                            {isSelf ? (
                              <Chip label={user.role} color={ROLE_COLORS[user.role]} size="small" />
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Select
                                  value={user.role}
                                  size="small"
                                  disabled={isUpdating}
                                  onChange={(e: SelectChangeEvent) =>
                                    handleRoleChange(user.uid, e.target.value as UserRole)
                                  }
                                  sx={{ minWidth: 110 }}
                                  aria-label={`Role for ${user.name}`}
                                >
                                  <MenuItem value="member">member</MenuItem>
                                  <MenuItem value="author">author</MenuItem>
                                  <MenuItem value="admin">admin</MenuItem>
                                </Select>
                                {isUpdating && <CircularProgress size={16} />}
                              </Box>
                            )}
                          </TableCell>

                          <TableCell align="right">
                            {!isSelf && (
                              <Tooltip title="Delete user profile">
                                <IconButton
                                  size="small"
                                  color="error"
                                  aria-label={`Delete ${user.name}`}
                                  onClick={() => openDeleteDialog('user', user.uid, user.name)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ── Projects tab ── */}
          {tab === 'projects' && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" aria-label="Project link management table">
                <TableHead>
                  <TableRow>
                    <TableCell>Project</TableCell>
                    <TableCell>Linked Blog Post</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projects.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                        No projects configured in projects.ts.
                      </TableCell>
                    </TableRow>
                  ) : (
                    projects.map(project => {
                      const isSaving = savingSlug === project.slug;
                      const selected = linkSelections[project.slug] ?? '';
                      const saved = savedLinks[project.slug] ?? '';
                      const isDirty = selected !== saved;
                      return (
                        <TableRow key={project.slug} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{project.title}</Typography>
                            <Typography variant="caption" color="text.secondary">{project.slug}</Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Select
                                value={selected}
                                size="small"
                                displayEmpty
                                disabled={isSaving || loading}
                                onChange={(e: SelectChangeEvent) =>
                                  setLinkSelections(prev => ({ ...prev, [project.slug]: e.target.value }))
                                }
                                sx={{ minWidth: 240 }}
                                aria-label={`Linked post for ${project.title}`}
                              >
                                <MenuItem value=""><em>— None —</em></MenuItem>
                                {posts.map(post => (
                                  <MenuItem key={post.id} value={post.id}>{post.title}</MenuItem>
                                ))}
                              </Select>
                              {isSaving && <CircularProgress size={16} />}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="contained"
                              disabled={isSaving || !isDirty}
                              onClick={() => handleSaveProjectLink(project.slug)}
                            >
                              Save
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ── Posts tab ── */}
          {tab === 'posts' && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" aria-label="Post management table">
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Author</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Published</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableSkeleton cols={5} />
                  ) : posts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                        No posts yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    posts.map(post => {
                      const isToggling = togglingPostId === post.id;
                      return (
                        <TableRow key={post.id} hover>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              component={RouterLink}
                              to={`/blog/${post.id}`}
                              style={{ color: 'inherit' }}
                            >
                              {post.title}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" color="text.secondary">{post.authorName}</Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" color="text.secondary">{formatDate(post.createdAt)}</Typography>
                          </TableCell>

                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Switch
                                size="small"
                                checked={post.published}
                                disabled={isToggling}
                                onChange={() => handleTogglePublished(post.id, post.published)}
                                aria-label={`Toggle published for "${post.title}"`}
                              />
                              <Chip
                                label={post.published ? 'Published' : 'Draft'}
                                color={post.published ? 'success' : 'default'}
                                size="small"
                              />
                              {isToggling && <CircularProgress size={14} />}
                            </Box>
                          </TableCell>

                          <TableCell align="right">
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                              <Tooltip title="Edit post">
                                <IconButton
                                  size="small"
                                  component={RouterLink}
                                  to={`/blog/${post.id}/edit`}
                                  aria-label={`Edit "${post.title}"`}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete post">
                                <IconButton
                                  size="small"
                                  color="error"
                                  aria-label={`Delete "${post.title}"`}
                                  onClick={() => openDeleteDialog('post', post.id, post.title)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* ── Delete confirmation dialog ── */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => !deleting && setDeleteDialog(d => ({ ...d, open: false }))}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          {deleteDialog.type === 'user' ? 'Delete user profile?' : 'Delete post?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteDialog.type === 'user' ? (
              <>
                This will permanently remove <strong>{deleteDialog.label}</strong>'s profile from the
                database. Their Firebase Auth account will remain — they can still sign in but will
                need to complete onboarding again.
              </>
            ) : (
              <>
                This will permanently delete the post <strong>"{deleteDialog.label}"</strong> and
                all of its comments. This cannot be undone.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog(d => ({ ...d, open: false }))}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
